import React from "react";
import { InfoIcon as InfoIconComponent } from "components/svg";
import WarningIcon from "components/svg/icons/WarningIcon";
import {
  PaymentTrackerContainer,
  StageWrapper,
  StageLabel,
  StageDate,
  ProgressBarContainer,
  ProgressLine,
  StageIndicator,
  InfoIcon,
  LabelWithIcon,
  WarningIconWrap,
  LateLabel,
} from "./styled";

export interface PaymentStage {
  label: string;
  date?: string;
  isCompleted?: boolean;
  isActive?: boolean;
  tooltip?: string;
  isLate?: boolean;
}

export interface PaymentTrackerProps {
  stages?: PaymentStage[];
  // For backwards compatibility, if specific props are passed:
  invoiceSentDate?: string;
  clientPaidDate?: string;
  paidDate?: string;
  currentStage?: "invoice_sent" | "client_paid" | "paid" | "not_started";
}

const PaymentTracker: React.FC<PaymentTrackerProps> = ({
  stages,
  invoiceSentDate,
  clientPaidDate,
  paidDate,
  currentStage = "not_started",
}) => {
  // Default stages if not provided
  const defaultStages: PaymentStage[] = [
    {
      label: "Invoice Sent to Client",
      date: invoiceSentDate,
      isCompleted: ["invoice_sent", "client_paid", "paid"].includes(currentStage),
      isActive: currentStage === "invoice_sent",
    },
    {
      label: "Client Paid",
      date: clientPaidDate,
      isCompleted: ["client_paid", "paid"].includes(currentStage),
      isActive: currentStage === "client_paid",
    },
    {
      label: "Paid",
      date: paidDate,
      isCompleted: currentStage === "paid",
      isActive: currentStage === "paid",
    },
  ];

  const paymentStages = stages || defaultStages;

  return (
    <PaymentTrackerContainer>
      {/* Progress bar background */}
      <ProgressBarContainer>
        {paymentStages.map((stage, index) => (
          <React.Fragment key={index}>
            {stage.isLate ? (
              <WarningIconWrap>
                <WarningIcon fill="#D32F2F" width="20px" height="20px" />
              </WarningIconWrap>
            ) : (
              <StageIndicator
                $isActive={stage.isActive}
                $isCompleted={stage.isCompleted}
              />
            )}
            {index < paymentStages.length - 1 && (
              <ProgressLine
                $isCompleted={
                  (stage.isCompleted && paymentStages[index + 1]?.isCompleted) ||
                  (stage.isCompleted && paymentStages[index + 1]?.isLate)
                }
              />
            )}
          </React.Fragment>
        ))}
      </ProgressBarContainer>

      {/* Stage labels and dates */}
      {paymentStages.map((stage, index) => {
        // Set alignment: first = flex-start, middle = center, last = flex-end
        let align: 'flex-start' | 'center' | 'flex-end' = 'center';
        if (index === 0) align = 'flex-start';
        if (index === paymentStages.length - 1) align = 'flex-end';

        return (
          <StageWrapper
            key={index}
            $isActive={stage.isActive}
            $isCompleted={stage.isCompleted}
            $isLate={stage.isLate}
            $align={align}
          >
            <LabelWithIcon>
              <StageLabel
                $isActive={stage.isActive}
                $isCompleted={stage.isCompleted}
                $isLate={stage.isLate}
              >
                {stage.label}
              </StageLabel>
              {stage.tooltip && (
                <InfoIcon title={stage.tooltip}>
                  <InfoIconComponent fill="#818181" width="10px" height="10px" />
                </InfoIcon>
              )}
            </LabelWithIcon>
            <StageDate
              $isActive={stage.isActive}
              $isCompleted={stage.isCompleted}
            >
              {stage.date || '-'}
              {stage.isLate && <LateLabel> - Late</LateLabel>}
            </StageDate>
          </StageWrapper>
        );
      })}
    </PaymentTrackerContainer>
  );
};

export default PaymentTracker;
